import { useEffect, useRef } from "react";

/**
 * Reveal helper. Wraps children and adds 'is-visible' class when in viewport.
 * - Reveals immediately on mount if the element is already in the viewport
 *   (fixes "only footer visible on first paint" when JS mounts after render).
 * - Safety fallback ensures content is always shown after ~1.2s, even if
 *   IntersectionObserver never fires (older browsers, prefetch, etc.).
 */
export default function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reveal = () => {
      if (!el.classList.contains("is-visible")) {
        el.classList.add("is-visible");
      }
    };

    // If element is already in the viewport on mount, reveal immediately.
    const rect = el.getBoundingClientRect();
    const inView =
      rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.bottom > 0;
    if (inView) {
      requestAnimationFrame(reveal);
    }

    // Fallback for browsers without IntersectionObserver.
    if (typeof window.IntersectionObserver === "undefined") {
      reveal();
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(reveal, delay);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(el);

    // Safety fallback: always reveal after 1.2s.
    const safety = setTimeout(reveal, 1200);

    return () => {
      obs.disconnect();
      clearTimeout(safety);
    };
  }, [delay]);

  return (
    <Tag ref={ref} className={`reveal ${className}`}>
      {children}
    </Tag>
  );
}
